import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { cmApi } from '@src/api';
import { dataControlStore } from '@stores/dataControlStore';
import type { TableComponentRequestDto } from '@src/api';

import styles from './ControlLevelFilter.module.scss';

export const ControlLevelFilter = observer(() => {
  const [runNumberItems, setRunNumberItems] = useState<{ id: string; name: string }[]>([]);
  const [controlTypeItems, setControlTypeItems] = useState<{ id: string; name: string }[]>([]);
  const [statusItems, setStatusItems] = useState<{ id: string; name: string }[]>([]);

  const { filters, setFilters, lastPagesParam, setLastPage, setNeedUpdateFilters, needUpdateFilters } =
    dataControlStore;

  const { controlType, status, runNumber } = filters.controlLevel || {};

  useEffect(
    () => () => {
      setNeedUpdateFilters(true);
    },
    [setNeedUpdateFilters],
  );

  useEffect(() => {
    if (needUpdateFilters) {
      const tableInfo = {
        aggregate: {},
        fields: ['exec_num', 'date_kp', 'user_kp'],
        filter: [],
        groupBy: [],
        hardFilter: [[...(lastPagesParam.filters?.[0] ?? [])]],
        limit: 1000000,
        offset: 0,
        openGroup: [],
        orderBy: [],
        resource: 'v_ctrl_pckg',
      };

      cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
        const result = response?.data as unknown as { data: { exec_num: string; date_kp: string; user_kp: string }[] };
        if (result && result.data) {
          const out = result.data
            .filter((item) => item.exec_num && item.exec_num !== '0')
            .map((item) => ({
              id: item.exec_num,
              name: `${item.exec_num} - ${dayjs(new Date(item.date_kp)).format('DD.MM.YYYY HH:mm:ss')} - ${
                item.user_kp
              }`,
            }));

          setRunNumberItems(out);
          setNeedUpdateFilters(false);
        }
      });
    }
  }, [lastPagesParam.filters, needUpdateFilters, setNeedUpdateFilters]);

  // если не передавался runNumber (Порядковый номер запуска КП), то выбираем последний
  useEffect(() => {
    setLastPage({ controlType: 'controlLevel', maxExecNum: runNumberItems.at(-1)?.id });

    if (!runNumber) {
      setFilters({ runNumber: runNumberItems.at(-1)?.id }, 'controlLevel');
    }
  }, [runNumber, runNumberItems, setFilters, setLastPage]);

  useEffect(() => {
    const tableInfo = {
      aggregate: {},
      fields: ['step_type_text'],
      filter: [],
      groupBy: [],
      hardFilter: [],
      limit: 100,
      offset: 0,
      openGroup: [],
      orderBy: [],
      resource: 'ctrl.nsi_step_type',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as { data: { step_type_text: string }[] };
      if (result && result.data && result.data.length > 0) {
        const out = result.data.map((item) => ({
          id: item.step_type_text,
          name: item.step_type_text,
        }));
        setControlTypeItems(out);
      }
    });
  }, []);

  useEffect(() => {
    const tableInfo = {
      fields: ['status', 'status_text'],
      hardFilter: [
        [
          {
            name: 'status_schema',
            type: FILTER.EQ,
            filterIsInverted: false,
            values: ['CTRL_1'],
          },
        ],
      ],
      limit: 100,
      offset: 0,
      resource: 'ctrl.nsi_ctrl_status',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as { data: { status: string; status_text: string }[] };
      if (result && result.data && result.data.length > 0) {
        const out = result.data.map((item) => ({
          id: item.status,
          name: `${item.status} - ${item.status_text}`,
        }));
        setStatusItems(out);
      }
    });
  }, []);

  const onChangeRunNumberItems = useCallback(
    (value) => {
      setFilters({ ...filters.controlLevel, runNumber: value }, 'controlLevel');
    },
    [filters.controlLevel, setFilters],
  );

  const onChangeControlType = useCallback(
    (value) => {
      setFilters({ ...filters.controlLevel, controlType: value }, 'controlLevel');
    },
    [filters.controlLevel, setFilters],
  );

  const onChangeStatus = useCallback(
    (value) => {
      setFilters({ ...filters.controlLevel, status: value }, 'controlLevel');
    },
    [filters.controlLevel, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    setFilters({}, 'controlLevel');
  }, [setFilters]);

  const disabled = useMemo(() => !Object.keys(filters.controlLevel ?? {}).length, [filters.controlLevel]);

  return (
    <div className={styles.designFilters}>
      <div className={styles.filterItems}>
        {runNumber !== '0' && (
          <Select
            items={runNumberItems}
            placeholder="Номер прогона"
            value={runNumber}
            size="small"
            onChange={onChangeRunNumberItems}
            showSearch
            className={styles.selectStatus}
            allowClear={false}
          />
        )}

        <Select
          items={controlTypeItems}
          placeholder="Тип контроля"
          value={controlType}
          size="small"
          onChange={onChangeControlType}
          showSearch
          className={styles.select}
        />

        <Select
          items={statusItems}
          placeholder="Статус"
          value={status}
          size="small"
          onChange={onChangeStatus}
          showSearch
          className={styles.selectStatus}
        />
      </div>

      <Button onClick={handleResetFilters} disabled={disabled} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
